/**
 * Add inCart ability to a model
 */

import { CART, SEATRADIUS } from '@/constants';
import SVG from 'svg.js';
import Application from '../application';
import { Row } from '../models/row';
import { Section } from '../models/sections/section';
import { Table } from '../models/tables/table';

export interface CartableRequirements {
  attributes: Record<string, any>;
  view: SVG.G;
  app: Application;
  selected: boolean;
  shape: SVG.Shape;
  categoryId: number | null | undefined;
  uuid: string;
  section?: Section | null | undefined;
}

export const CartableMixin = <T extends { new (...args: any[]): CartableRequirements }>(base: T) => {
  return class CartableMixin extends base {
    inCardShape: SVG.Path | null = null;

    // Only known when a shape...
    declare sid: string;
    declare row?: Row;
    declare table?: Table;

    checkInCart({
      sectionId,
      categoryId,
      rowLabel,
      label,
    }: {
      sectionId?: string | number;
      categoryId: number | string | undefined;
      rowLabel: string;
      label: string;
    }) {
      if (sectionId && !(sectionId == this.sid || sectionId == this.section?.attributes.serverSectionId)) {
        return false;
      }

      return (
        label === this.attributes.label &&
        categoryId == this.categoryId &&
        rowLabel == (this.row ?? this.table)?.attributes.label
      );
    }

    get inCart() {
      return (
        this.app.props.seatsInCartById?.includes(this.uuid) ||
        this.app.props.seatsInCartByLabel?.some((a) => this.checkInCart(a))
      );
    }

    set inCart(value) {
      if (value) {
        this.app.props.seatsInCartById = [...(this.app.props.seatsInCartById ?? []), this.uuid];
      } else {
        this.app.props.seatsInCartById = this.app.props.seatsInCartById?.filter((id) => id !== this.uuid);
        this.app.props.seatsInCartByLabel = this.app.props.seatsInCartByLabel?.filter(
          ({ sectionId, categoryId, label }) => {
            if (sectionId && sectionId != this.sid) {
              return true;
            }

            return label !== this.attributes.label || categoryId != this.categoryId;
          }
        );
      }

      // this.attributes.status = value ? SEATSTATUS.CART : SEATSTATUS.FREE;
      this.toggleCartIconVisibility();
    }

    toggleCartIconVisibility() {
      if (this.inCart) {
        this.initCartShape();
      } else {
        this.removeCartShape();
      }
    }

    initCartShape([cx, cy] = this.attributes.position) {
      if (this.inCardShape?.node) return;

      this.removeCartShape();

      const size = (SEATRADIUS - 2) * this.app.seatmap.scale;

      this.inCardShape = this.view
        .path(CART.SHAPE)
        .size(size)
        .center(cx, cy)
        .stroke({ color: CART.STROKE, width: 2 })
        .fill('none');
    }

    removeCartShape() {
      this.inCardShape && this.inCardShape.remove();
      this.inCardShape = null;
    }

    updateCartShape(x: number, y: number) {
      if (this.inCardShape) {
        const size = (SEATRADIUS - 2) * this.app.seatmap.scale;
        this.inCardShape.size(size).center(x, y);
      }
    }

    addToCart() {
      this.selected = false;
      this.inCart = true;
      this.view.removeClass('free-seat').removeClass('selected-seat').addClass('cart-seat');
      this.shape.removeClass('selected').addClass('cart');
    }
  };
};
